import type { T } from './i18n-core';

/**
 * 계정 입력 유효성 — **현행 규칙 그대로**다(2026-08-22 사용자 지시 "유효성 체크는 현행에 따르면 된다").
 * 원본: 현행 `views/member/register2.php` 의 jQuery validate rules.
 *
 * | 칸 | 현행 규칙 |
 * |---|---|
 * | 아이디 | `^[a-zA-Z][a-zA-Z0-9]+$` + 4~16자 + 중복확인 |
 * | 비밀번호 | 영문·숫자·특수문자(@$!%*#?&_) 각 1자 이상 + 8자 이상 |
 * | 비밀번호 확인 | 같아야 함 |
 * | 이름 | 한글·영문·숫자 + 2~16자 |
 * | 이메일 | 형식 + 중복확인 |
 * | 휴대폰 | 숫자만 + 9자 이상 |
 * | 생년월일 | 숫자 8자 |
 * | 우편번호 | 5자 이상 · 주소 10자 이상 |
 *
 * ⚠ 서버 DTO 는 아이디 4~16 영문/숫자/_ 를 받는다(밑줄 허용·첫 글자 제약 없음).
 * 화면은 **현행 규칙이 더 좁아** 그대로 두면 서버가 거절할 일이 없다(좁은 쪽이 통과 기준).
 */
const RE = {
  uid: /^[a-zA-Z][a-zA-Z0-9]+$/,
  password: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&_])[A-Za-z\d@$!%*#?&_]{8,}$/,
  name: /^[가-힣a-zA-Z0-9]+$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  digits: /^\d+$/,
};

export interface SignupInput {
  uid: string; password: string; passwordConfirm: string; name: string;
  email: string; mobile: string; birthday: string;
  zipcode: string; address: string; addressDetail: string;
}

/** 칸 이름 → 오류 문구. 비어 있으면 통과 */
export type Errors = Partial<Record<keyof SignupInput | 'authCode' | 'agree', string>>;

/**
 * 아이디 한 칸만 검사 — 중복확인 버튼과 저장이 **같은 판정**을 쓰게 뺐다.
 * (2026-08-22: 중복확인이 형식을 안 보고 서버에 물어 한글도 "사용 가능" 으로 보였다)
 */
export function uidError(uid: string, t: T): string | null {
  const v = uid.trim();
  if (!v) return t('acc.err.uidRequired');
  if (!RE.uid.test(v) || v.length < 4 || v.length > 16) return t('acc.err.uidFormat');
  return null;
}

export function validateSignup(f: SignupInput, t: T, opts: { needsAddress?: boolean } = {}): Errors {
  const e: Errors = {};
  const uidErr = uidError(f.uid, t);
  if (uidErr) e.uid = uidErr;

  if (!f.password) e.password = t('acc.err.pwRequired');
  else if (!RE.password.test(f.password)) e.password = t('acc.err.pwFormat');
  if (f.password !== f.passwordConfirm) e.passwordConfirm = t('acc.err.pwMismatch');

  if (!f.name.trim()) e.name = t('acc.err.nameRequired');
  else if (!RE.name.test(f.name) || f.name.length < 2 || f.name.length > 16) e.name = t('acc.err.nameFormat');

  if (!f.email.trim()) e.email = t('acc.err.emailRequired');
  else if (!RE.email.test(f.email)) e.email = t('acc.err.emailFormat');

  if (!f.mobile.trim()) e.mobile = t('acc.err.mobileRequired');
  else if (!RE.digits.test(f.mobile) || f.mobile.length < 9) e.mobile = t('acc.err.mobileFormat');

  if (!f.birthday.trim()) e.birthday = t('acc.err.birthdayRequired');
  else if (!RE.digits.test(f.birthday) || f.birthday.length !== 8) e.birthday = t('acc.err.birthdayFormat');

  // 주소는 현행에서 필수다. 다만 우편번호 검색을 끈 마켓은 직접 입력이라 같은 기준으로 본다
  if (opts.needsAddress !== false) {
    if (f.zipcode.trim().length < 5) e.zipcode = t('acc.err.zipcode');
    if (`${f.address} ${f.addressDetail}`.trim().length < 10) e.address = t('acc.err.address');
  }
  return e;
}

/** 비밀번호만 — 비밀번호 변경·재설정 화면 공용 */
export function validatePassword(pw: string, confirm: string, t: T): { password?: string; passwordConfirm?: string } {
  const e: { password?: string; passwordConfirm?: string } = {};
  if (!pw) e.password = t('acc.err.pwRequired');
  else if (!RE.password.test(pw)) e.password = t('acc.err.pwFormat');
  if (pw !== confirm) e.passwordConfirm = t('acc.err.pwMismatch');
  return e;
}

export const isEmail = (v: string) => RE.email.test(v);
export const isDigits = (v: string) => RE.digits.test(v);
export const firstError = (e: Errors): string | null => Object.values(e).find((v) => !!v) ?? null;
